import { useState } from "react";
import { ExternalLink, Mail, Building2, MapPin, Briefcase } from "lucide-react";
import {
  type Prospect,
  PROSPECT_STATUS_LABELS,
  PROSPECT_STATUS_COLORS,
  companyFaviconUrl,
  prospectInitials,
} from "@/lib/types";
import { formatRelativeTime } from "@/lib/utils";

/**
 * Tuile prospect — inspirée des cartes "service" du thème site aurora-ds.com.
 * Layout : entête (avatar/favicon + statut + métadonnée), corps (identité +
 * entreprise), preview accroche, footer (CTA / dernière maj).
 */
export function ProspectTile({ prospect: p }: { prospect: Prospect }) {
  const fullName = [p.first_name, p.last_name].filter(Boolean).join(" ") || "—";
  const favicon = companyFaviconUrl(p.company_website, 64);
  const initials = prospectInitials(p);
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <article
      className="aurora-tile-white group flex h-full flex-col p-5 cursor-pointer"
      onClick={() => window.open(`/prospects/${p.id}`, "_blank", "noopener")}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          window.open(`/prospects/${p.id}`, "_blank", "noopener");
        }
      }}
    >
      {/* === Header : avatar + statut === */}
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md bg-aurora-gray ring-1 ring-neutral-200">
          {favicon && !imgFailed ? (
            <img
              src={favicon}
              alt={p.company_name ?? ""}
              className="size-7"
              loading="lazy"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <span className="text-sm font-bold text-aurora-navy">{initials}</span>
          )}
        </div>

        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${PROSPECT_STATUS_COLORS[p.status]}`}
        >
          {PROSPECT_STATUS_LABELS[p.status]}
        </span>
      </div>

      {/* === Identité + entreprise === */}
      <div className="mb-3">
        <h3 className="text-base font-semibold leading-tight text-aurora-navy transition-colors group-hover:text-aurora-orange">
          {fullName}
        </h3>
        {p.job_title && (
          <p className="mt-0.5 flex items-center gap-1.5 text-sm text-neutral-600">
            <Briefcase className="size-3 shrink-0 text-neutral-400" />
            <span className="truncate">{p.job_title}</span>
          </p>
        )}
      </div>

      <div className="mb-3 space-y-1 text-sm text-neutral-600">
        <div className="flex items-center gap-1.5">
          <Building2 className="size-3 shrink-0 text-neutral-400" />
          <span className="truncate font-medium text-neutral-700">{p.company_name ?? "—"}</span>
          {p.company_website && (
            <a
              href={p.company_website}
              target="_blank"
              rel="noreferrer noopener"
              className="text-neutral-400 hover:text-aurora-bright-blue"
              onClick={(e) => e.stopPropagation()}
              aria-label="Site web"
            >
              <ExternalLink className="size-3" />
            </a>
          )}
        </div>
        {p.industry && (
          <div className="flex items-center gap-1.5">
            <span className="inline-flex rounded bg-aurora-gray px-1.5 py-0.5 text-xs text-aurora-teal">
              {p.industry}
            </span>
            {p.city && (
              <span className="flex items-center gap-1 text-xs text-neutral-500">
                <MapPin className="size-3" />
                {p.city}
              </span>
            )}
          </div>
        )}
      </div>

      {/* === Preview accroche Claude === */}
      {p.personalization_subject && (
        <div className="mb-4 rounded-md border border-neutral-100 bg-neutral-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-neutral-400">Sujet proposé</div>
          <p className="mt-0.5 line-clamp-2 text-sm text-neutral-700">
            {p.personalization_subject}
          </p>
        </div>
      )}

      {/* === Footer : email + dernière maj === */}
      <div className="mt-auto flex items-center justify-between border-t border-neutral-100 pt-3 text-xs text-neutral-500">
        <a
          href={`mailto:${p.email}`}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1.5 truncate hover:text-aurora-bright-blue"
          title={p.email}
        >
          <Mail className="size-3 shrink-0" />
          <span className="truncate">{p.email}</span>
        </a>
        <span className="shrink-0 pl-2 text-neutral-400">{formatRelativeTime(p.updated_at)}</span>
      </div>
    </article>
  );
}
